import _ from "lodash";
import { AppCard } from "@lib/components/AppComponents";
import { useIntl } from "react-intl";
import { ActionButton } from "../../Buttons";
import { Empty, Skeleton, Typography } from "antd";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { CurrencyEnum, PaginationType, UseQueryProps } from "@lib/types";
import { AILog } from "@lib/types/AILog";
import { useMemo } from "react";
import { useTheme } from "styled-components";
import { getContrastColor, randomColor } from "@lib/helpers/ColorHelper";
import {
  currencySymbol,
  formatCurrency,
  shortenString,
} from "@lib/helpers/StringHelper";
import { StyledFlex } from "../../Table/index.styled";

type Props = {
  query?: UseQueryProps<PaginationType<AILog>>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const AIModelCostChart = (props: Props) => {
  const { query, extra, navigate, loading, refetch } = props;
  const { mode } = useTheme();
  const { data, isLoading, isFetching } = query || {};
  const { messages } = useIntl();

  const chartData = data?.data?.data || [];

  const modelIds = _.uniq(chartData.map((item) => item.modelId));
  // const customerNames = _.uniq(chartData.map((item) => item.customer?.name));

  const labelText = (modelId: string) => {
    return (
      shortenString(modelId, 20, true) +
      " :  " +
      formatCurrency(
        chartData
          .filter((item) => item.modelId === modelId)
          .reduce((acc, item) => acc + item.cost, 0),
      ) +
      " " +
      currencySymbol(CurrencyEnum.USD)
    );
  };

  const chartSeries: ApexOptions["series"] = useMemo(() => {
    const series: ApexOptions["series"] = [
      {
        data: modelIds.map((modelId) => {
          return chartData
            .filter((item) => item.modelId === modelId)
            .reduce((acc, item) => acc + item.cost, 0)
            .toFixed(2) as unknown as number;
        }),
      },
    ];

    return series;
  }, [chartData]);

  const chartOptions = useMemo(() => {
    return {
      theme: {
        mode: mode,
      },
      chart: {
        type: "bar",
        height: 400,
        width: 400,
      },
      plotOptions: {
        bar: {
          barHeight: "100%",
          distributed: true,
          horizontal: true,
          dataLabels: {
            position: "bottom",
          },
        },
      },
      colors: modelIds.map((v, i) => {
        return randomColor(i);
      }),
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: modelIds.map((v, i) => {
            return getContrastColor(randomColor(i));
          }),
        },
        formatter: function (val, opt) {
          return labelText(modelIds[opt.dataPointIndex]);
        },
        offsetX: 0,
        dropShadow: {
          enabled: true,
        },
      },
      tooltip: {
        custom: function ({ dataPointIndex }) {
          return `<div style="padding: 10px;">
            <div>${labelText(modelIds[dataPointIndex])}</div>
          </div>`;
        },
        shared: true,
        intersect: false,
      },
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      xaxis: {
        categories: modelIds,
        labels: {
          show: true,
          formatter: (val: number) => {
            return `${val} ${currencySymbol(CurrencyEnum.USD)}`;
          },
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
    } as ApexOptions;
  }, [modelIds, mode, messages]);

  return (
    <AppCard
      title={messages["common.aiModelCostChart"] as string}
      extra={
        <StyledFlex style={{ gap: 10 }}>
          {extra}
          <>
            {navigate && (
              <Typography.Link onClick={() => navigate()}>
                {messages["common.viewAll"] as string}
              </Typography.Link>
            )}
          </>
          {refetch && (
            <ActionButton
              type="refresh"
              size="small"
              onClick={() => refetch()}
              disabled={loading}
              loading={loading}
            />
          )}
        </StyledFlex>
      }
    >
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {chartData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type={chartOptions.chart?.type}
              height={chartOptions.chart?.height || 400}
            />
          )}
        </>
      )}
    </AppCard>
  );
};

export default AIModelCostChart;
